export type SwipeEventType =
	| 'swiperight'
	| 'swipedown'
	| 'swipeup'
	| 'swipeleft'
	| 'none'

export type Positions = {
	startX: number
	startY: number
	lastX: number
	lastY: number
	x: number
	y: number
}

export type SwipeSideEffects = {
	onSwipeStart?: (e: TouchEvent) => void
	onSwipeMove?: (e: TouchEvent, positions: Positions) => void
	onSwipeEnd?: (
		e: TouchEvent,
		positions: Positions,
		eventtype: SwipeEventType
	) => void
}

export type SwipeDestroyer = {
	destroy: () => void
}

const swipeThreshold = 60

const between = (n: number, min: number, max: number): boolean =>
	n >= min && n < max

const isInDirection = (
	θ: number,
	lastθ: number,
	minθ: number,
	maxθ: number
): boolean => between(θ, minθ, maxθ) && between(lastθ, minθ, maxθ)

const getSwipeDirection = (positions: Positions): SwipeEventType => {
	const π = Math.PI
	const θ = Math.atan2(
		positions.y - positions.startY,
		positions.x - positions.startX
	)
	const lastθ = Math.atan2(
		positions.y - positions.lastY,
		positions.x - positions.lastX
	)
	const r = Math.hypot(
		positions.x - positions.startX,
		positions.y - positions.startY
	)

	if (r <= swipeThreshold) return 'none'

	if (isInDirection(θ, lastθ, -π * 0.25, π * 0.25)) return 'swiperight'
	if (isInDirection(θ, lastθ, π * 0.25, π * 0.75)) return 'swipedown'
	if (isInDirection(θ, lastθ, -π * 0.75, -π * 0.25)) return 'swipeup'
	if (isInDirection(Math.abs(θ), Math.abs(lastθ), π * 0.75, π + 0.0001))
		return 'swipeleft'

	return 'none'
}

/**
 * @description
 * Adds a swipe event listener to an element. Tracks touch gestures and
 * dispatches a custom event when the gesture matches the specified direction.
 *
 * @param element - The element to listen for touch events on
 * @param eventtype - The swipe direction to listen for
 * @param sideEffects - Optional callbacks for swipe start, move, and end
 * @returns A destroyer object with a destroy() method to remove listeners
 */
export const addSwipeEvent = (
	element: HTMLElement,
	eventtype: SwipeEventType,
	{
		onSwipeStart = () => {},
		onSwipeMove = () => {},
		onSwipeEnd = () => {}
	}: SwipeSideEffects = {}
): SwipeDestroyer => {
	let startPos: number[]
	let lastPos: number[]
	let willFire: SwipeEventType | undefined
	let positions: Positions | undefined

	const onTouchStart = (e: TouchEvent) => {
		startPos = lastPos = [
			e.changedTouches[0].screenX,
			e.changedTouches[0].screenY
		]
		positions = undefined
		willFire = undefined

		element.addEventListener('touchmove', onTouchMove, { passive: false })
		element.addEventListener('touchend', onTouchEnd)

		onSwipeStart(e)
	}

	const onTouchMove = (e: TouchEvent) => {
		const [startX, startY] = startPos,
			[lastX, lastY] = lastPos,
			[x, y] = [e.changedTouches[0].screenX, e.changedTouches[0].screenY]

		positions = { startX, startY, lastX, lastY, x, y }

		willFire = getSwipeDirection(positions)
		onSwipeMove(e, positions)
		lastPos = [x, y]
	}

	const onTouchEnd = (e?: TouchEvent) => {
		if (willFire === eventtype && e) {
			element.dispatchEvent(new Event(willFire))
		}

		element.removeEventListener('touchmove', onTouchMove)
		element.removeEventListener('touchend', onTouchEnd)

		onSwipeEnd(e, positions, willFire)
	}

	const destroy = () => {
		element.removeEventListener('touchstart', onTouchStart)
		onTouchEnd()
	}

	element.addEventListener('touchstart', onTouchStart)

	return { destroy }
}
